import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface LegalPageShellProps {
  /** Document title (e.g. "Privacy Policy"). */
  title: string;
  /** Effective date — ISO format (YYYY-MM-DD). */
  effectiveDate: string;
  /** Document version string matching CONSENT_TYPES version constants. */
  version: string;
  /** Body content — typically a sequence of <section> blocks. */
  children: ReactNode;
}

/**
 * LegalPageShell — read-only layout for public-facing legal documents.
 *
 * Used by `/privacy` and `/terms`. Intentionally separate from `AuthShell`:
 * legal pages are reading surfaces, not forms; they need long-form prose,
 * permanent navigation back to login, and an explicit version + effective
 * date on screen.
 *
 * Rendering decisions:
 *  - Prose is rendered with Tailwind's `prose` rules via local utility classes
 *    (the project does not currently include `@tailwindcss/typography`).
 *  - Maximum content width caps reading line-length at ~70ch for legibility.
 *  - The version + effective-date band is sticky enough to stay visible while
 *    the reader scrolls — important for proving they saw the version they
 *    later consented to (KDPA §32).
 *  - A "Print or save as PDF" button surfaces window.print(); a focused
 *    @media print stylesheet (in client/src/styles/global.css) hides nav
 *    chrome, expands content, and prevents sections breaking mid-page.
 *
 * Linked from: login.tsx footer; ActivationPage consent step;
 * ResidentSelfRegistrationPage consent step.
 */
export function LegalPageShell({ title, effectiveDate, version, children }: LegalPageShellProps) {
  return (
    <main className="legal-shell min-h-svh bg-background text-foreground">
      <header className="legal-shell__header border-b border-[var(--border-default)] bg-[var(--bg-surface)]">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link
            to="/login"
            className="text-2xl leading-none tracking-tight text-[var(--text-accent)] brand-name"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Secure Labs
          </Link>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => window.print()}
              className="legal-shell__print-button text-sm underline underline-offset-4 hover:text-[var(--text-accent)]"
              aria-label="Print or save this document as PDF"
            >
              Print / save as PDF
            </button>
            <Link
              to="/login"
              className="legal-shell__back-link text-sm underline underline-offset-4 hover:text-[var(--text-accent)]"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </header>

      <article className="legal-shell__article mx-auto max-w-3xl px-6 py-10">
        <h1 className="text-3xl tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-[var(--text-secondary)]">
          Effective date: <span className="font-medium">{effectiveDate}</span> · Version{' '}
          <span className="font-medium">{version}</span>
        </p>

        <div className="mt-8 space-y-6 text-[15px] leading-relaxed text-[var(--text-primary)]">
          {children}
        </div>

        <footer className="mt-12 border-t border-[var(--border-default)] pt-6 text-sm text-[var(--text-secondary)]">
          <p>
            Questions about this document? Contact the Secure Labs Data Protection Officer at{' '}
            <a
              href="mailto:dpo@securelabsystems.com"
              className="underline underline-offset-4 hover:text-[var(--text-accent)]"
            >
              dpo@securelabsystems.com
            </a>
            .
          </p>
          <p className="mt-2">
            Secure Labs Limited · P.O. Box 8456-00100, Tom Mboya Street, Nairobi, Kenya · Registered
            Data Controller, Office of the Data Protection Commissioner (Kenya).
          </p>
        </footer>
      </article>
    </main>
  );
}
